/**
 * Blotato referral link helper.
 *
 * The referral parameter (e.g. `ref=frank`) is defined in exactly one place:
 * backend `.env` → `BLOTATO_REFERRAL_PARAM`. The frontend fetches it once via
 * `GET /api/config/blotato-referral` and caches it in module scope.
 *
 * Usage:
 *   import { buildBlotatoLink, primeBlotatoReferral } from "@/lib/blotato";
 *   // In app boot (App.jsx), call primeBlotatoReferral() once.
 *   <a href={buildBlotatoLink()}>blotato.com</a>
 *   <a href={buildBlotatoLink("/pricing")}>Blotato pricing</a>
 *   <a href={buildBlotatoLink("/signup?src=email")}>Sign up</a>  // safely
 *                                                                merges &ref=…
 */
import api from "@/lib/api";

const BASE = "https://blotato.com";
let cachedParam = ""; // e.g. "ref=frank" or "" if not configured

/**
 * Fetch the referral param from the backend once and cache it.
 * Idempotent — safe to call multiple times.
 * @returns {Promise<string>} the raw param string (may be "")
 */
export async function primeBlotatoReferral() {
  if (cachedParam) return cachedParam;
  try {
    const r = await api.get("/config/blotato-referral");
    cachedParam = String(r?.data?.param || "");
  } catch {
    cachedParam = "";
  }
  return cachedParam;
}

/**
 * Build a fully-qualified subscriber-facing blotato.com URL with the
 * referral param appended. Uses `?` when the path has no existing query
 * string and `&` when it does, so page-specific query params keep working.
 *
 * NEVER used for `api.blotato.com` / `backend.blotato.com/v2` — those calls
 * live in `backend/integrations/blotato_client.py` and must not receive
 * marketing params.
 *
 * @param {string} [path]  optional relative path (e.g. "/pricing"). Empty
 *                         string / undefined → root domain.
 * @returns {string} fully-qualified URL, e.g.
 *                   "https://blotato.com/pricing?ref=frank"
 */
export function buildBlotatoLink(path = "") {
  const clean = path && !path.startsWith("/") ? `/${path}` : (path || "");
  const url = `${BASE}${clean}`;
  if (!cachedParam) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${cachedParam}`;
}
